import "./AccountContractNotesComponent.scss";
import template from "./AccountContractNotesComponent.html";
import {IComponentOptions, IOnDestroy} from "angular";
import {Observable, ReplaySubject} from "rxjs";
import {ContractNoteRecord, ContractNoteRecords} from "../../types/ContractNoteRecord";
import {IsoDate} from "../../../pseudo-types/DateTypes";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";
import moment from "moment";
import {ContractNotesService} from "../../services/ContractNotesService";
import {ArrayUtil} from "../../../js/util/ArrayUtil";
import {descendingUsing} from "../../../js/util/UtilFunctions";
import {AccountId} from "../../types/AccountRecord";
import FileSaver from "file-saver";

export const AccountContractNotes = "tfAccountContractNotes";

export function accountContractNotes(): IComponentOptions {
    return {
        template,
        bindings: {
            accountId: "<"
        },
        controllerAs: "ctrl",
        controller: AccountContractNotesComponentController
    };
}

export class AccountContractNotesComponentController implements IOnDestroy {

    /** @input */
    public set accountId(accountId: AccountId | null) {
        if (accountId != null) {
            this.accountId$.next(accountId);
        }
    }

    private readonly accountId$ = new ReplaySubject<AccountId>(1);

    public readonly toDate$ = new ReplaySubject<IsoDate>();
    public readonly fromDate$ = new ReplaySubject<IsoDate>();

    public readonly cNotes$: Observable<ContractNoteRecords>;

    private readonly export$ = new ReplaySubject<boolean>(1);
    private readonly destroy$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly contractNotesService: ContractNotesService) {
        this.initializeDates();
        this.cNotes$ = this.buildCNoteStream();

        this.export$
            .withLatestFrom(this.cNotes$)
            .takeUntil(this.destroy$)
            .subscribe(([_, cnotes]) => {
                this.exportCnotesToCsv(cnotes, "cnotes.csv");
            });
    }

    private initializeDates() {
        const toDate = DateTimeUtil.momentToIsoDate(moment());
        this.toDate$.next(toDate);
        const from = moment().subtract(1, "year");
        const fromDate = DateTimeUtil.momentToIsoDate(from);
        this.fromDate$.next(fromDate);
    }

    private buildCNoteStream(): Observable<ContractNoteRecords> {
        const source$ = Observable.combineLatest(this.accountId$, this.fromDate$, this.toDate$);

        return source$.switchMap(([acc, fromDate, toDate]) => this.retrieveCNotes(acc, fromDate, toDate))
                      .map((transactions) => ArrayUtil.clone(transactions).sort(descendingUsing((a) => a.contractDate)))
                      .publishReplay(1)
                      .refCount();
    }

    private retrieveCNotes(accountId: AccountId,
                           fromDate: IsoDate,
                           toDate: IsoDate): Observable<ContractNoteRecords> {
        const response = this.contractNotesService.list(accountId, fromDate, toDate);
        return Observable.fromPromise(response)
                         .catch(() => Observable.empty());
    }

    public updateToDate(date: IsoDate | null) {
        if (date != null) {
            this.toDate$.next(date);
        }
    }

    public updateFromDate(date: IsoDate | null) {
        if (date != null) {
            this.fromDate$.next(date);
        }
    }

    public exportToCsv() {
        this.export$.next(true);
    }

    private exportCnotesToCsv(cnotes: ContractNoteRecords, fileName: string) {
        const header = `Date,Settlement Date,Reference,Code,Side,Quantity,Average Price,Value,Brokerage,Status`;
        const body = cnotes.map((model) => this.formatCnoteAsCsv(model))
                           .join("\n");
        const csv = header + "\n" + body;

        const options = {type: "text/csv"};
        const blob = new Blob([csv], options);
        FileSaver.saveAs(blob, fileName);
    }

    private formatCnoteAsCsv(cnote: ContractNoteRecord) {
        const asAt = cnote.asAtDate;
        const settleDate = cnote.settleDate;
        const ref = cnote.referenceNumber;
        const securityCode = cnote.securityCode;
        const buySell = cnote.buySell;
        const quantity = cnote.quantity;
        const averagePrice = cnote.averagePrice;
        const consideration = cnote.consideration;
        const brokerage = cnote.brokerage + cnote.gst;
        const status = cnote.status;

        return [asAt, settleDate, ref, securityCode, buySell, quantity, averagePrice, consideration, brokerage, status].join(",");
    }

    public $onDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
}
